import { BubbleMenu, Editor, isTextSelection } from "@tiptap/react";
import TextInput from "../../../../ui/TextInput/TextInput";
import Button from "../../../../ui/Button/Button";
import {
  Bold,
  Italic,
  Strikethrough,
  Link as LinkIcon,
  Underline as UnderlineIcon,
  Paragraph,
  Heading2,
  MessageTextSquare,
  NumberList,
  BulletList,
  Heading4,
  Heading3,
  Trash,
  LinkExternal,
} from "../../../../ui/Icon/Icon";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import Dropdown from "../../../../ui/Dropdown/Dropdown";
import styles from "./CustomBubbleMenu.module.scss";

function isMacOS(): boolean {
  return typeof navigator !== "undefined"
    ? /Mac/.test(navigator.platform)
    : false;
}

function ExternalLinkButton({ href }: { href: string }) {
  return (
    <a
      className={styles.externalLinkButton}
      target="_blank"
      href={href}
      rel="noreferrer"
    >
      <LinkExternal />
    </a>
  );
}

function BubbleMenuContent({
  editor,
  showLinkOptions,
  setShowLinkOptions,
}: {
  editor: Editor;
  showLinkOptions: boolean;
  setShowLinkOptions: (show: boolean) => void;
}) {
  const { dropdownLabel, DropdownIcon } = useMemo(() => {
    const fromParent = editor.state.selection.$from.node(2);
    const toParent = editor.state.selection.$to.node(2);

    if (
      !fromParent ||
      !toParent ||
      fromParent?.type.name !== toParent?.type.name
    ) {
      return { DropdownIcon: null, dropdownLabel: null };
    }

    const type = fromParent.type.name;

    let hideLabel: boolean = false;

    editor.state.doc.nodesBetween(
      editor.state.selection.from,
      editor.state.selection.to,
      (node, _, parent) => {
        if (parent?.type.name !== "section") {
          return;
        }

        if (node.type.name !== type) {
          hideLabel = true;
        }
      },
    );

    if (hideLabel) {
      return { dropdownLabel: null, DropdownIcon: null };
    }

    if (type === "paragraph") {
      return { dropdownLabel: "Paragraph", DropdownIcon: Paragraph };
    }

    if (type === "heading") {
      if (fromParent.attrs.level === 2) {
        return {
          dropdownLabel: "Heading 2",
          DropdownIcon: Heading2,
        };
      }

      if (fromParent.attrs.level === 3) {
        return {
          dropdownLabel: "Heading 3",
          DropdownIcon: Heading3,
        };
      }

      if (fromParent.attrs.level === 4) {
        return {
          dropdownLabel: "Heading 4",
          DropdownIcon: Heading4,
        };
      }
    }

    if (type === "bulletList") {
      return { dropdownLabel: "Bulleted list", DropdownIcon: BulletList };
    }

    if (type === "orderedList") {
      return { dropdownLabel: "Numbered list", DropdownIcon: NumberList };
    }

    if (type === "blockquote") {
      return { dropdownLabel: "Quote", DropdownIcon: MessageTextSquare };
    }

    return { DropdownIcon: null, dropdownLabel: null };
  }, [
    editor.state.doc,
    editor.state.selection.$from,
    editor.state.selection.$to,
    editor.state.selection.from,
    editor.state.selection.to,
  ]);

  if (showLinkOptions) {
    return (
      <div
        className={styles.linkOptions}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        key={editor.getAttributes("link").href}
      >
        <div className={styles.inputWrapper}>
          <TextInput
            autofocus
            placeholder="Add URL"
            rightSection={
              editor.getAttributes("link").href ? (
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                <ExternalLinkButton href={editor.getAttributes("link").href} />
              ) : null
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            defaultValue={editor.getAttributes("link").href ?? ""}
            onBlur={() => {
              editor.chain().setTextSelection(editor.state.selection).run();
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter") {
                return;
              }

              let url = e.currentTarget.value;

              // cancelled
              if (url === null) {
                return;
              }

              // empty
              if (url === "") {
                editor
                  .chain()
                  .focus()
                  .extendMarkRange("link")
                  .unsetLink()
                  .run();
                setShowLinkOptions(false);

                return;
              }

              if (!url.startsWith("http://") && !url.startsWith("https://")) {
                url = `https://${url}`;
              }

              // update link
              editor
                .chain()
                .focus()
                .extendMarkRange("link")
                .setLink({ href: url })
                .run();
              setShowLinkOptions(false);
            }}
          />
        </div>
        {editor.getAttributes("link").href && (
          <Button
            className={styles.linkOptionButton}
            leftIcon={<Trash />}
            onClick={() => {
              editor.chain().focus().extendMarkRange("link").unsetLink().run();
              setShowLinkOptions(false);
            }}
          >
            Remove link
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      {dropdownLabel && (
        <Dropdown
          variant="unstyled"
          className={styles.button}
          items={[
            {
              items: [
                {
                  label: "Paragraph",
                  IconComponent: Paragraph,
                  onClick: () => editor.chain().focus().setParagraph().run(),
                },
                {
                  label: "Heading 2",
                  IconComponent: Heading2,
                  onClick: () =>
                    editor.chain().focus().setHeading({ level: 2 }).run(),
                },
                {
                  label: "Heading 3",
                  IconComponent: Heading3,
                  onClick: () =>
                    editor.chain().focus().setHeading({ level: 3 }).run(),
                },
                {
                  label: "Heading 4",
                  IconComponent: Heading4,
                  onClick: () =>
                    editor.chain().focus().setHeading({ level: 4 }).run(),
                },
                {
                  label: "Bulleted list",
                  IconComponent: BulletList,
                  onClick: () =>
                    editor.chain().focus().toggleBulletList().run(),
                },
                {
                  label: "Numbered list",
                  IconComponent: NumberList,
                  onClick: () =>
                    editor.chain().focus().toggleOrderedList().run(),
                },
                {
                  label: "Quote",
                  IconComponent: MessageTextSquare,
                  onClick: () =>
                    editor.chain().focus().toggleBlockquote().run(),
                },
              ],
            },
          ]}
        >
          <DropdownIcon className={styles.dropdownIcon} /> {dropdownLabel}
        </Dropdown>
      )}
      <Button
        className={classNames(styles.button, {
          [styles.isActive]: editor.isActive("link"),
        })}
        leftIcon={<LinkIcon />}
        onClick={() => {
          setShowLinkOptions(true);
        }}
      >
        Link
      </Button>
      <Button
        variant="unstyled"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={classNames(styles.iconButton, {
          [styles.isActive]: editor.isActive("bold"),
        })}
      >
        <Bold />
      </Button>
      <Button
        variant="unstyled"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={classNames(styles.iconButton, {
          [styles.isActive]: editor.isActive("italic"),
        })}
      >
        <Italic />
      </Button>
      <Button
        variant="unstyled"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={classNames(styles.iconButton, {
          [styles.isActive]: editor.isActive("underline"),
        })}
      >
        <UnderlineIcon />
      </Button>
      <Button
        variant="unstyled"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={classNames(styles.iconButton, {
          [styles.isActive]: editor.isActive("strike"),
        })}
      >
        <Strikethrough />
      </Button>
    </>
  );
}

function CustomBubbleMenu({ editor }: { editor: Editor }) {
  const [showLinkOptions, setShowLinkOptions] = useState(false);

  useEffect(() => {
    const showLinkOptionsShortcut = (e: KeyboardEvent) => {
      const modKey = isMacOS() ? e.metaKey : e.ctrlKey;

      if (e.key === "k" && modKey) {
        setShowLinkOptions(true);
      }

      if (e.key === "Escape") {
        setShowLinkOptions(false);
      }
    };

    document.addEventListener("keydown", showLinkOptionsShortcut);

    return () =>
      document.removeEventListener("keydown", showLinkOptionsShortcut);
  }, [setShowLinkOptions]);

  useEffect(() => {
    if (editor.state.selection.to !== editor.state.selection.from) {
      setShowLinkOptions(false);
    }
  }, [editor.state.selection.from, editor.state.selection.to]);

  return (
    <BubbleMenu
      className={styles.bubbleMenu}
      tippyOptions={{
        duration: 100,
        maxWidth: 400,
        delay: 2000,
        onShow: () => {
          if (
            editor.isActive("link") &&
            editor.state.selection.to === editor.state.selection.from
          ) {
            setShowLinkOptions(true);
          }
        },
        onAfterUpdate: () => {
          if (
            editor.isActive("link") &&
            editor.state.selection.to === editor.state.selection.from
          ) {
            setShowLinkOptions(true);
          }
        },
        onHidden: () => {
          setShowLinkOptions(false);
        },
      }}
      editor={editor}
      shouldShow={({ view, state, from, to }) => {
        const { doc, selection } = state;
        const { empty } = selection;

        // Sometime check for `empty` is not enough.
        // Double click an empty paragraph returns a node size of 2.
        // So we check also for an empty text size.
        const isEmptyTextBlock =
          !doc.textBetween(from, to).length && isTextSelection(state.selection);

        let shouldShow = true;

        doc.nodesBetween(from, to, (node) => {
          if (!shouldShow) {
            return false;
          }

          if (["sectionTitle", "title", "excerpt"].includes(node.type.name)) {
            shouldShow = false;
          }
        });

        if (!shouldShow) {
          return false;
        }

        // When clicking on a element inside the bubble menu the editor "blur" event
        // is called and the bubble menu item is focussed. In this case we should
        // consider the menu as part of the editor and keep showing the menu
        const isChildOfMenu = view.dom.contains(document.activeElement);

        const hasEditorFocus = view.hasFocus() || isChildOfMenu;

        if (editor.isActive("link") && isEmptyTextBlock) {
          return true;
        }

        if (
          !hasEditorFocus ||
          empty ||
          isEmptyTextBlock ||
          !editor.isEditable ||
          editor.isActive("image")
        ) {
          return false;
        }

        return true;
      }}
    >
      <BubbleMenuContent
        editor={editor}
        showLinkOptions={showLinkOptions}
        setShowLinkOptions={setShowLinkOptions}
      />
    </BubbleMenu>
  );
}

export default CustomBubbleMenu;
